import React from "react";
import styles from "../../../CSS/ui/Button.module.css";

/**
 * Flexible Button component
 *
 * Props:
 * - label: string (button text)
 * - url?: string (renders as <a> when provided)
 * - variant?: 'solid' | 'outline' (default: 'solid')
 * - bgColor?: string (CSS color like '#fff' or 'rgba(...)')
 * - textColor?: string (CSS color like '#123')
 * - borderColor?: string (optional; used for outline variant; CSS color)
 * - leftIcon?: React.ElementType (Lucide icon component)
 * - rightIcon?: React.ElementType (Lucide icon component)
 * - iconSize?: number (default: 18)
 * - className?: string (additional classes)
 * - ...props: button/anchor props
 */
const Button = ({
  label,
  url,
  variant = "solid",
  bgColor,
  textColor,
  borderColor,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  iconSize = 18,
  className = "",
  disabled = false,
  target,
  ...props
}) => {
  // Inline style overrides for bg/text/border colors when provided
  const isCssColor = (v) =>
    typeof v === "string" &&
    (v.startsWith("#") || v.startsWith("rgb") || v.startsWith("hsl"));

  const style = {};
  if (bgColor && isCssColor(bgColor)) style.backgroundColor = bgColor;
  if (textColor && isCssColor(textColor)) style.color = textColor;
  if (borderColor && isCssColor(borderColor)) style.borderColor = borderColor;

  // Base + variant classes (CSS Module + optional Bootstrap 'btn')
  const baseClasses = `${styles.btnCustom}`;
  const variantClass = variant === "outline" ? styles.outline : styles.solid;

  const Comp = url ? "a" : "button";

  return (
    <Comp
      href={url}
      target={target}
      aria-label={typeof label === "string" ? label : undefined}
      className={`${baseClasses} ${variantClass} ${className}`.trim()}
      style={style}
      disabled={disabled}
      {...props}
    >
      {LeftIcon ? (
        <LeftIcon size={iconSize} strokeWidth={2} className={styles.icon} />
      ) : null}
      {label}
      {RightIcon ? (
        <RightIcon size={iconSize} strokeWidth={2} className={styles.icon} />
      ) : null}
    </Comp>
  );
};

export default Button;
